import type { DrawingKind, MagnetMode } from "lightweight-charts-drawing"
import { type ReactNode, useEffect, useRef, useState } from "react"
import {
  CursorIcon,
  EyeIcon,
  EyeOffIcon,
  FibIcon,
  ForecastIcon,
  MagnetIcon,
  PatternIcon,
  PinIcon,
  ShapeIcon,
  TextIcon,
  TrashIcon,
  TrendIcon
} from "../../ui/icons.tsx"
import { drawingToolbar, useDrawingToolbar } from "./drawingToolbarStore.ts"
import { DRAWING_TOOL_GROUPS, type DrawingTool } from "./tools.ts"
import { toolFavorites, useToolFavorites } from "./toolFavorites.ts"
import { isTypingIn } from "../../ui/typing.ts"

// TradingView's left toolbar: one button per tool group with a flyout of its
// tools, then magnet, keep drawing, hide all, and remove all.

const GROUP_ICONS: Readonly<Record<string, () => ReactNode>> = {
  "Trend tools": TrendIcon,
  "Gann and Fibonacci tools": FibIcon,
  Patterns: PatternIcon,
  "Forecasting and measurement tools": ForecastIcon,
  "Geometric shapes": ShapeIcon,
  "Annotation tools": TextIcon
}

const GROUPS = [...new Set(DRAWING_TOOL_GROUPS.map((g) => g.group))]
const ALL_TOOLS = DRAWING_TOOL_GROUPS.flatMap((g) => g.tools)
const toolByKind = new Map(ALL_TOOLS.map((tool) => [tool.kind, tool]))
const groupOf = (kind: DrawingKind | null) => DRAWING_TOOL_GROUPS.find((g) => g.tools.some((t) => t.kind === kind))?.group

/** "Alt + T" → matches Alt+T key presses. */
const matchesHotkey = (tool: DrawingTool, event: KeyboardEvent) => {
  const [modifier, key] = (tool.hotkey ?? "").split(" + ")
  return modifier === "Alt" && event.altKey && !event.ctrlKey && !event.metaKey && event.code === `Key${key}`
}

/** Space between a group's button and its open flyout, and between the flyout and the window's bottom edge. */
const FLYOUT_GAP = 6

const MAGNET_NEXT: Readonly<Record<MagnetMode, MagnetMode>> = { off: "weak", weak: "strong", strong: "off" }

export function DrawingToolbar() {
  const toolbar = useDrawingToolbar()
  const favorites = useToolFavorites()
  // The open flyout and where its button is: the flyout is `position: fixed` there, because the
  // toolbar scrolls on short windows and would clip anything positioned inside it.
  const [open, setOpen] = useState<{ readonly group: string; readonly top: number; readonly left: number } | null>(null)
  const openGroup = open?.group ?? null
  const lastUsed = useRef(new Map<string, DrawingKind>())
  const rootRef = useRef<HTMLElement>(null)

  const arm = (kind: DrawingKind) => {
    const group = groupOf(kind)
    if (group !== undefined) lastUsed.current.set(group, kind)
    drawingToolbar.setTool(kind)
    setOpen(null)
  }

  // Hotkeys (Alt+T trend line, Alt+F fib retracement, …) and closing the flyout.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (isTypingIn(document.activeElement)) return
      const tool = ALL_TOOLS.find((t) => matchesHotkey(t, event))
      if (tool !== undefined) {
        event.preventDefault()
        arm(tool.kind)
      } else if (event.key === "Escape") setOpen(null)
    }
    const onPointer = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(null)
    }
    window.addEventListener("keydown", onKey)
    window.addEventListener("pointerdown", onPointer)
    return () => {
      window.removeEventListener("keydown", onKey)
      window.removeEventListener("pointerdown", onPointer)
    }
  }, [])

  // Scrolling the toolbar moves the buttons but not the flyout (it is `fixed`): close it then. A scroll
  // event that arrives after the flyout opened (the browser scrolled the button into view first) leaves
  // the button where the flyout was placed, and must not close it.
  const closeIfAnchorMoved = () => {
    if (open === null) return
    const anchor = rootRef.current?.querySelector('[aria-expanded="true"]')
    if (anchor === null || anchor === undefined || anchor.getBoundingClientRect().top !== open.top) setOpen(null)
  }

  const armedGroup = groupOf(toolbar.tool)

  return (
    <nav ref={rootRef} className="drawing-toolbar" aria-label="Drawing tools" onScroll={closeIfAnchorMoved}>
      <button
        type="button"
        title="Cursor"
        aria-pressed={toolbar.tool === null}
        onClick={() => drawingToolbar.setTool(null)}
      >
        <CursorIcon />
      </button>

      {GROUPS.map((group) => {
        const GroupIcon = GROUP_ICONS[group] ?? TrendIcon
        const remembered = lastUsed.current.get(group)
        return (
          <div key={group} className="tool-group">
            <button
              type="button"
              title={remembered ? `${group}: ${toolByKind.get(remembered)?.name}` : group}
              aria-pressed={armedGroup === group}
              aria-expanded={openGroup === group}
              onClick={(event) => {
                const rect = event.currentTarget.getBoundingClientRect()
                setOpen(openGroup === group ? null : { group, top: rect.top, left: rect.right + FLYOUT_GAP })
              }}
            >
              <GroupIcon />
            </button>
            {open?.group === group && (
              <div
                className="tool-flyout"
                role="menu"
                style={{ top: open.top, left: open.left, maxHeight: `calc(100vh - ${open.top}px - ${FLYOUT_GAP}px)` }}
              >
                {DRAWING_TOOL_GROUPS.filter((g) => g.group === group).map((sub) => (
                  <section key={sub.subgroup}>
                    <h3>{sub.subgroup}</h3>
                    {sub.tools.map((tool) => {
                      const favorite = favorites.includes(tool.kind)
                      return (
                        <div key={tool.kind} className="tool-row">
                          <button
                            type="button"
                            role="menuitem"
                            aria-pressed={toolbar.tool === tool.kind}
                            onClick={() => arm(tool.kind)}
                          >
                            <span>{tool.name}</span>
                            {tool.hotkey && <kbd>{tool.hotkey}</kbd>}
                          </button>
                          <button
                            type="button"
                            className={`tool-star${favorite ? " tool-star-on" : ""}`}
                            aria-pressed={favorite}
                            aria-label={favorite ? `Remove ${tool.name} from favorites` : `Add ${tool.name} to favorites`}
                            title={favorite ? "Remove from favorites" : "Add to favorites"}
                            onClick={() => toolFavorites.toggle(tool.kind)}
                          >
                            {favorite ? "★" : "☆"}
                          </button>
                        </div>
                      )
                    })}
                  </section>
                ))}
              </div>
            )}
          </div>
        )
      })}

      <span className="toolbar-divider" />

      <button
        type="button"
        title={`Magnet: ${toolbar.magnet} (click to change)`}
        aria-pressed={toolbar.magnet !== "off"}
        className={toolbar.magnet === "strong" ? "magnet-strong" : undefined}
        onClick={() => drawingToolbar.setMagnet(MAGNET_NEXT[toolbar.magnet])}
      >
        <MagnetIcon />
      </button>
      <button
        type="button"
        title="Keep drawing (the tool stays selected after a drawing)"
        aria-pressed={toolbar.stayInDrawingMode}
        onClick={() => drawingToolbar.setStayInDrawingMode(!toolbar.stayInDrawingMode)}
      >
        <PinIcon />
      </button>
      <button
        type="button"
        title={toolbar.drawingsVisible ? "Hide all drawings" : "Show all drawings"}
        aria-pressed={!toolbar.drawingsVisible}
        onClick={() => drawingToolbar.setDrawingsVisible(!toolbar.drawingsVisible)}
      >
        {toolbar.drawingsVisible ? <EyeIcon /> : <EyeOffIcon />}
      </button>
      <button
        type="button"
        title="Remove all drawings of this symbol"
        onClick={() => {
          if (window.confirm("Remove every drawing on this symbol?")) drawingToolbar.requestClear()
        }}
      >
        <TrashIcon />
      </button>
    </nav>
  )
}
